(function () {
  const focusableSelector =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class MoonPromoPopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.sectionId = this.dataset.sectionId;
      this.modal = this.querySelector('[data-promo-modal]');
      this.dialog = this.querySelector('[data-promo-dialog]');
      this.overlay = this.querySelector('[data-promo-overlay]');
      this.closeButtons = this.querySelectorAll('[data-promo-close]');
      this.launcher = this.querySelector('[data-promo-launcher]');
      this.launcherTrigger = this.querySelector('[data-promo-launcher-trigger]');
      this.launcherClose = this.querySelector('[data-promo-launcher-close]');
      this.copyButton = this.querySelector('[data-promo-copy]');
      this.emailInput = this.querySelector('input[type="email"]');
      this.lastFocusedElement = null;
      this.openTimer = null;
      this.alignmentFrame = null;

      this.dismissedKey = `moon-sutras-promo-dismissed-${this.sectionId}`;
      this.launcherDismissedKey = `moon-sutras-promo-launcher-dismissed-${this.sectionId}`;
      this.launcherSessionDismissedKey = `moon-sutras-promo-launcher-dismissed-session-${this.sectionId}`;
      this.signedUpKey = `moon-sutras-promo-signed-up-${this.sectionId}`;
      this.sessionShownKey = `moon-sutras-promo-session-shown-${this.sectionId}`;

      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleResize = this.handleResize.bind(this);

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.close({ rememberDismissal: true }));
      });

      if (this.launcherTrigger) {
        this.launcherTrigger.addEventListener('click', () => this.open({ source: 'launcher' }));
      } else if (this.launcher) {
        this.launcher.addEventListener('click', () => this.open({ source: 'launcher' }));
      }

      if (this.launcherClose) {
        this.launcherClose.addEventListener('click', (event) => this.dismissLauncher(event));
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', () => {
          if (this.bool('closeOnBackdrop')) this.close({ rememberDismissal: true });
        });
      }

      if (this.copyButton) {
        this.copyButton.addEventListener('click', () => this.copyDiscountCode());
      }

      window.addEventListener('resize', this.handleResize);

      if (this.hasCompletedSignup()) {
        this.dataset.success = 'true';
      }

      if (this.dataset.signupSuccess === 'true') {
        this.setSignedUp();
        this.dataset.success = 'true';
        requestAnimationFrame(() => this.open({ source: 'success', force: true }));
      } else if (this.dataset.formError === 'true') {
        requestAnimationFrame(() => this.open({ source: 'error', force: true }));
      } else {
        this.scheduleAutoOpen();
      }

      this.updateLauncher();
      this.updateLauncherAlignment();

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => this.queueLauncherAlignmentUpdate());
      }

      if (document.readyState !== 'complete') {
        window.addEventListener('load', () => this.queueLauncherAlignmentUpdate(), { once: true });
      }
    }

    disconnectedCallback() {
      clearTimeout(this.openTimer);
      if (this.alignmentFrame) cancelAnimationFrame(this.alignmentFrame);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('keydown', this.handleKeydown);
    }

    bool(name) {
      return this.dataset[name] === 'true';
    }

    isDesignMode() {
      return Boolean(window.Shopify && window.Shopify.designMode);
    }

    isMobile() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    popupEnabledForViewport() {
      if (!this.bool('enabled')) return false;
      return this.isMobile() ? this.bool('enableMobilePopup') : this.bool('enableDesktopPopup');
    }

    launcherEnabledForViewport() {
      if (!this.bool('enabled') || !this.bool('launcherEnabled')) return false;
      return this.isMobile() ? this.bool('enableMobileLauncher') : this.bool('enableDesktopLauncher');
    }

    getDismissedAt() {
      try {
        return Number(localStorage.getItem(this.dismissedKey) || 0);
      } catch (error) {
        return 0;
      }
    }

    getLauncherDismissedAt() {
      try {
        return Number(localStorage.getItem(this.launcherDismissedKey) || 0);
      } catch (error) {
        return 0;
      }
    }

    getLauncherSessionDismissed() {
      if (this.dataset.launcherDismissed === 'true') return true;

      try {
        return sessionStorage.getItem(this.launcherSessionDismissedKey) === 'true';
      } catch (error) {
        return false;
      }
    }

    getSignedUp() {
      try {
        return localStorage.getItem(this.signedUpKey) === 'true';
      } catch (error) {
        return false;
      }
    }

    getSessionShown() {
      try {
        return sessionStorage.getItem(this.sessionShownKey) === 'true';
      } catch (error) {
        return false;
      }
    }

    hasCompletedSignup() {
      return this.getSignedUp() || this.bool('existingSubscriber');
    }

    setSignedUp() {
      try {
        localStorage.setItem(this.signedUpKey, 'true');
      } catch (error) {
        return;
      }
    }

    rememberDismissal() {
      const value = String(Date.now());

      try {
        localStorage.setItem(this.dismissedKey, value);
      } catch (error) {
        return;
      }

      this.rememberSessionShown(value);
    }

    rememberLauncherDismissal() {
      const value = String(Date.now());
      this.dataset.launcherDismissed = 'true';

      if (this.isDesignMode()) return;

      try {
        localStorage.setItem(this.launcherDismissedKey, value);
        sessionStorage.setItem(this.launcherSessionDismissedKey, 'true');
      } catch (error) {
        return;
      }
    }

    rememberSessionShown(value = String(Date.now())) {
      if (this.isDesignMode()) return;

      try {
        sessionStorage.setItem(this.sessionShownKey, 'true');
        sessionStorage.setItem(`${this.sessionShownKey}-at`, value);
      } catch (error) {
        return;
      }
    }

    shouldAutoOpen() {
      if (!this.bool('autoOpen') || !this.popupEnabledForViewport()) return false;
      if (this.hasCompletedSignup()) return false;
      if (this.getSessionShown()) return false;

      const showAgainAfter = this.dataset.showAgainAfter || '7';

      if (showAgainAfter === 'every_visit') {
        return true;
      }

      const dismissedAt = this.getDismissedAt();
      if (!dismissedAt) return true;
      if (showAgainAfter === 'never') return false;

      const days = Number(showAgainAfter);
      if (!days) return true;

      return Date.now() - dismissedAt >= days * 24 * 60 * 60 * 1000;
    }

    scheduleAutoOpen() {
      clearTimeout(this.openTimer);

      if (this.isDesignMode()) return;
      if (!this.shouldAutoOpen()) return;

      const delay = Number(this.dataset.delay || 3000);
      this.openTimer = setTimeout(() => this.open({ source: 'auto' }), delay);
    }

    launcherDismissalActive() {
      const showAgainAfter = this.dataset.launcherShowAgainAfter || '7';

      if (showAgainAfter === 'next_visit') {
        return this.getLauncherSessionDismissed();
      }

      const dismissedAt = this.getLauncherDismissedAt();
      if (!dismissedAt) return this.dataset.launcherDismissed === 'true';
      if (showAgainAfter === 'never') return true;

      const days = Number(showAgainAfter);
      if (!days) return false;

      return Date.now() - dismissedAt < days * 24 * 60 * 60 * 1000;
    }

    shouldShowLauncher() {
      if (!this.launcherEnabledForViewport()) return false;
      if (this.hasCompletedSignup()) return false;
      if (this.launcherDismissalActive()) return false;

      const signedUp = this.getSignedUp() || this.dataset.success === 'true';
      if (signedUp && !this.bool('showLauncherAfterSignup')) return false;

      const dismissedAt = this.getDismissedAt();
      if (dismissedAt && !this.bool('showLauncherAfterDismissal') && !signedUp) return false;

      return true;
    }

    updateLauncher() {
      if (!this.launcher) return;
      this.launcher.hidden = !this.shouldShowLauncher();
    }

    getHeroCta() {
      const heroButtons = Array.from(document.querySelectorAll('main [class*="ai-image-banner__button"]'));
      const visibleHeroButton = heroButtons.find((element) => this.isVisibleElement(element));

      if (visibleHeroButton) return visibleHeroButton;

      return Array.from(document.querySelectorAll('main a, main button')).find((element) => {
        return (element.textContent || '').trim().toUpperCase() === 'SHOP NOW' && this.isVisibleElement(element);
      });
    }

    isVisibleElement(element) {
      const rect = element.getBoundingClientRect();
      const styles = window.getComputedStyle(element);

      return rect.width > 0 && rect.height > 0 && styles.display !== 'none' && styles.visibility !== 'hidden';
    }

    updateLauncherAlignment() {
      if (!this.launcher) return;

      if (this.isMobile()) {
        this.style.removeProperty('--msp-launcher-hero-left');
        this.style.removeProperty('--msp-launcher-hero-width');
        return;
      }

      const heroCta = this.getHeroCta();
      if (!heroCta) {
        this.style.removeProperty('--msp-launcher-hero-left');
        this.style.removeProperty('--msp-launcher-hero-width');
        return;
      }

      const heroRect = heroCta.getBoundingClientRect();
      const left = heroRect.left;
      const width = heroRect.width;

      if (!Number.isFinite(left) || left < 0 || !Number.isFinite(width) || width <= 0) {
        this.style.removeProperty('--msp-launcher-hero-left');
        this.style.removeProperty('--msp-launcher-hero-width');
        return;
      }

      this.style.setProperty('--msp-launcher-hero-left', `${Math.round(left)}px`);
      this.style.setProperty('--msp-launcher-hero-width', `${width.toFixed(2)}px`);
    }

    dismissLauncher(event) {
      event?.preventDefault();
      event?.stopPropagation();
      this.rememberLauncherDismissal();
      this.updateLauncher();
    }

    queueLauncherAlignmentUpdate() {
      if (this.alignmentFrame) return;

      this.alignmentFrame = requestAnimationFrame(() => {
        this.alignmentFrame = null;
        this.updateLauncherAlignment();
      });
    }

    open({ source = 'manual', force = false } = {}) {
      if (!force && !this.popupEnabledForViewport()) return;
      if (this.dataset.open === 'true') return;

      clearTimeout(this.openTimer);
      this.lastFocusedElement = document.activeElement;
      this.dataset.open = 'true';
      this.modal?.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.handleKeydown);

      if (source === 'auto' || source === 'launcher' || source === 'manual') {
        this.rememberSessionShown();
      }

      requestAnimationFrame(() => {
        const showSuccess = this.dataset.success === 'true';
        const focusTarget =
          source === 'success' || showSuccess
            ? this.querySelector('[data-success-focus]') || this.dialog
            : this.emailInput || this.dialog;

        focusTarget?.focus();
      });
    }

    close({ rememberDismissal = false } = {}) {
      if (this.dataset.open !== 'true') return;

      this.dataset.open = 'false';
      this.modal?.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.handleKeydown);

      if (rememberDismissal) this.rememberDismissal();
      this.updateLauncher();

      if (this.lastFocusedElement && document.contains(this.lastFocusedElement)) {
        this.lastFocusedElement.focus();
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.bool('closeOnEscape')) {
        event.preventDefault();
        this.close({ rememberDismissal: true });
        return;
      }

      if (event.key !== 'Tab' || !this.dialog) return;

      const focusable = Array.from(this.dialog.querySelectorAll(focusableSelector)).filter(
        (element) => element.offsetParent !== null || element === document.activeElement
      );

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleResize() {
      this.updateLauncher();
      this.queueLauncherAlignmentUpdate();

      if (this.dataset.open === 'true' && !this.popupEnabledForViewport()) {
        this.close({ rememberDismissal: false });
      }
    }

    copyDiscountCode() {
      const code = this.copyButton?.dataset.code;
      if (!code) return;

      const originalText = this.copyButton.textContent;
      const copiedText = this.copyButton.dataset.copiedText || originalText;
      const setCopied = () => {
        this.copyButton.textContent = copiedText;
        window.setTimeout(() => {
          this.copyButton.textContent = originalText;
        }, 2200);
      };

      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(code).then(setCopied).catch(() => this.fallbackCopy(code, setCopied));
      } else {
        this.fallbackCopy(code, setCopied);
      }
    }

    fallbackCopy(code, callback) {
      const textarea = document.createElement('textarea');
      textarea.value = code;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
      callback();
    }
  }

  if (!customElements.get('moon-promo-popup')) {
    customElements.define('moon-promo-popup', MoonPromoPopup);
  }

  document.addEventListener('shopify:section:select', (event) => {
    const popup = event.target.querySelector('moon-promo-popup');
    if (popup) popup.open({ source: 'editor', force: true });
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const popup = event.target.querySelector('moon-promo-popup');
    if (popup) popup.close({ rememberDismissal: false });
  });
})();
